import { useForceUpdate } from "@mantine/hooks";
import { useMemo, useRef } from "react";

import {
  type AcceptedValue,
  type ArrayKeys,
  type AssertValidParams,
  type ElementOf,
  FilterParams,
  type FilterParamsReadable,
  type ParamsConstraint,
} from "./filter-params";

export interface FilterParamsHandle<
  T extends ParamsConstraint,
> extends FilterParamsReadable<T> {
  set<K extends keyof T>(key: K, value: T[K]): FilterParamsHandle<T>;
  append<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParamsHandle<T>;
  delete<K extends keyof T>(key: K): FilterParamsHandle<T>;
  delete<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParamsHandle<T>;
  toggleValue<K extends ArrayKeys<T>>(
    key: K,
    value: ElementOf<T[K]>,
  ): FilterParamsHandle<T>;
  replaceAll(
    newState: Partial<T & AssertValidParams<T>>,
  ): FilterParamsHandle<T>;
  clear(): FilterParamsHandle<T>;
}

export const useFilterParams = <T extends ParamsConstraint>(
  initialState?: Partial<T & AssertValidParams<T>>,
): FilterParamsHandle<T> => {
  const paramsRef = useRef(new FilterParams<T>(initialState));
  const forceUpdate = useForceUpdate();

  // Memoize handle to provide stable reference. Methods access paramsRef
  // (always current) and forceUpdate (stable from Mantine), so empty deps is safe.
  const handle: FilterParamsHandle<T> = useMemo(() => {
    const h: FilterParamsHandle<T> = {
      get: (key) => paramsRef.current.get(key),

      getAll: (key) => paramsRef.current.getAll(key),

      getWithDefault: (key, defaultValue) =>
        paramsRef.current.getWithDefault(key, defaultValue),

      has: ((key: keyof T, value?: AcceptedValue) =>
        paramsRef.current.has(
          key as ArrayKeys<T>,
          value as ElementOf<T[ArrayKeys<T>]>,
        )) as FilterParamsHandle<T>["has"],

      hasAnyOf: (keys) => paramsRef.current.hasAnyOf(keys),

      set: (key, value) => {
        paramsRef.current = paramsRef.current.set(key, value);
        forceUpdate();
        return h;
      },

      append: (key, value) => {
        paramsRef.current = paramsRef.current.append(key, value);
        forceUpdate();
        return h;
      },

      delete: ((key: keyof T, value?: AcceptedValue) => {
        if (value === undefined) {
          paramsRef.current = paramsRef.current.delete(key);
        } else {
          paramsRef.current = paramsRef.current.delete(
            key as ArrayKeys<T>,
            value as ElementOf<T[ArrayKeys<T>]>,
          );
        }
        forceUpdate();
        return h;
      }) as FilterParamsHandle<T>["delete"],

      toggleValue: (key, value) => {
        paramsRef.current = paramsRef.current.toggleValue(key, value);
        forceUpdate();
        return h;
      },

      replaceAll: (newState) => {
        paramsRef.current = paramsRef.current.replaceAll(newState);
        forceUpdate();
        return h;
      },

      clear: () => {
        paramsRef.current = paramsRef.current.clear();
        forceUpdate();
        return h;
      },

      toString: () => paramsRef.current.toString(),

      toObject: () => paramsRef.current.toObject(),

      keys: () => paramsRef.current.keys(),

      values: () => paramsRef.current.values(),

      entries: () => paramsRef.current.entries(),

      get size() {
        return paramsRef.current.size;
      },
    };
    return h;

    // Current ref and forceUpdate are both stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return handle;
};
